/**
 * StackViewport — cornerstone STACK viewport for non-reconstructable series.
 *
 * Used for series whose `is_reconstructable` predicate is false (single-
 * instance scouts, 2D X-rays, secondary captures, SIEMENS-MPR degenerate-
 * IPP series, irregular-spacing series). The router (pickViewportComponent)
 * picks this component over VolumeViewport based on that one boolean.
 *
 * Mirrors VolumeViewport's lifecycle pattern (one-time enable + listeners,
 * content effect that swaps imageIds on series change) so a series swap
 * doesn't tear down the viewport. Stack scroll, pan, zoom, and W/L are
 * driven by the shared tool group — no per-viewport tool wiring here.
 *
 * Deliberately omits: volume creation, MPR coordination, IPP/IOP/spacing
 * registration, segmentation labelmap, streaming-volume loading state.
 * None of those apply to 2D acquisitions.
 */
import React, { useEffect, useRef, useState } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import {
  StudyMetadataResponse,
  buildS3ImageIds,
  registerImagePlaneMetadataForSeries,
  registerImagePixelMetadataForSeries,
  registerGeneralSeriesMetadataForSeries,
  registerVoiLutMetadataForSeries,
} from "../utils/dicomMetadataLoader";
import {
  initializeCornerstone,
  getCornerstone,
  getCornerstoneTools,
  getEnums,
} from "../utils/dicomLoader";
import {
  SHARED_RENDERING_ENGINE_ID,
  getOrCreateSharedRenderingEngine,
  disableSharedViewport,
} from "../utils/renderingEngine";
import { on as viewerBusOn, off as viewerBusOff } from "../lib/viewerBus";
import { derivePlane } from "../utils/seriesClassification";
import {
  applyRadiologicalFlip,
  paneNeedsRadiologicalFlip,
} from "../viewport/radiologicalOrientation";
import {
  configureToolGroup,
  switchActiveTool,
  STACK_SAFE_TOOLS,
  ensureContourSegmentationActive,
} from "../utils/viewportToolSetup";
import { activeMeasurementToolAtom } from "../atoms/measurementAtoms";
import {
  segmentationVolumeIdAtom,
  activeSegmentIndexAtom,
  isDrawingAtom,
  hasFirstStrokeAtom,
  lockedSeriesIdAtom,
  annotationStartTsAtom,
  isSegmentationActiveAtom,
  nnInteractiveLoadingAtom,
  bumpRadiusAtom,
} from "../atoms/segmentationAtoms";
import {
  setRegionGrowAutoSwitchCallback,
  setRegionGrowCommitCallback,
} from "../tools/RegionGrowTool";
import { setBumpRadius as setBumpRadiusModule } from "../tools/BumpContourTool";
import { useSegmentationCursor } from "../hooks/useSegmentationCursor";
import { resolveStackStartIndex } from "./stackStartIndex";
import { logger } from "@/utils/logger";

// Tools that need an active contour-segmentation rep. Safe on a stack pane
// ONLY when segmentation has been set up (enableSegmentation) — see the
// seg-setup effect below.
const SEG_TOOLS = new Set(["regionGrow", "lasso", "bump"]);

// Wait this long for a lost WebGL context to be restored before escalating the
// transient "restoring" message to the reload prompt. Mirrors VolumeViewport.
const RECOVERY_FALLBACK_MS = 8000;

interface StackViewportProps {
  seriesId: string;
  studyMetadata: StudyMetadataResponse;
  studyId: string;
  viewportIdOverride?: string;
  windowCenter?: number;
  windowWidth?: number;
  externalSliceIndex?: number;
  /** The slot's load-time cross-series jump target (a finding-jump slice),
   *  distinct from the current slice. Used to start a freshly-swapped stack at
   *  the jump target instead of the middle slice. */
  pendingJump?: number | null;
  isInverted?: boolean;
  resetCameraTrigger?: number;
  /** When true, build a CPU-side derived-volume labelmap + activate a contour
   *  segmentation on this stack pane so region-grow/lasso/bump work. Passed
   *  only for the weak-GPU fallback on a reconstructable series — NOT for
   *  geometry (is_reconstructable=false) fallbacks, where region-grow is
   *  unsafe. See pickViewportComponent + project CLAUDE.md. */
  enableSegmentation?: boolean;
  onSliceChange?: (slice: number, total: number) => void;
  onWindowChange?: (center: number, width: number) => void;
  onZoomChange?: (zoom: number) => void;
  onLoadingStateChange?: (
    isLoading: boolean,
    loadedFrames: number,
    totalFrames: number,
    isViewportDisplayable: boolean,
  ) => void;
}

const StackViewport: React.FC<StackViewportProps> = ({
  seriesId,
  studyMetadata,
  studyId,
  viewportIdOverride,
  windowCenter,
  windowWidth,
  externalSliceIndex,
  pendingJump,
  isInverted,
  resetCameraTrigger,
  enableSegmentation,
  onSliceChange,
  onWindowChange,
  onZoomChange,
  onLoadingStateChange,
}) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const viewportIdRef = useRef<string | null>(null);
  const imageIdsRef = useRef<string[]>([]);
  // The seriesId whose imageIds are currently loaded via setStack. Guards the
  // content effect so windowing/prop re-runs don't re-issue setStack (which
  // would reset the displayed slice to the start index and clobber a
  // finding-jump or manual scroll). Only a genuine series change reloads.
  const loadedStackSeriesRef = useRef<string | null>(null);
  // #coronal-lr: the flip decision made for the currently-loaded stack, so any
  // later path that clears the camera (Reset View) can re-assert the SAME answer
  // instead of re-deriving it and risking a divergent one.
  const shouldFlipHorizontalRef = useRef(false);
  const lastAppliedWindowRef = useRef<{ center: number; width: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lifecycleReady, setLifecycleReady] = useState(false);
  // Mirror of VolumeViewport: true while a WebGL context loss is recovering so
  // the content effect short-circuits before its setError(null) clobbers the
  // transient message. Cleared on webglContextRestored (self-heal).
  const contextLostRef = useRef(false);
  // Escalates the transient "restoring" message to the reload prompt if the
  // context is never restored.
  const recoveryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Bumped on webglContextRestored to force the content effect to re-run and
  // re-issue setStack (the displayed frame's texture was dropped with the
  // context, so a re-render alone would leave the pane blank).
  const [recoveryEpoch, setRecoveryEpoch] = useState(0);
  const activeMeasurementTool = useAtomValue(activeMeasurementToolAtom);
  const setSegmentationVolumeId = useSetAtom(segmentationVolumeIdAtom);
  const segmentationVolumeId = useAtomValue(segmentationVolumeIdAtom);
  // Ref-mirror so the seg-setup effect's cleanup can read the current atom
  // value without re-binding on every change. Used to "last-writer-wins"
  // clear the atom on unmount only when it still points at our id.
  const segmentationVolumeIdRef = useRef(segmentationVolumeId);
  segmentationVolumeIdRef.current = segmentationVolumeId;
  const activeSegmentIndex = useAtomValue(activeSegmentIndexAtom);
  // Setters used by the region-grow commit callback to flip the form's
  // drawing-state gates (Save enable, series lock, start-ts) when a seed
  // lands on this stack pane. Mirrors VolumeViewport.
  const setIsDrawing = useSetAtom(isDrawingAtom);
  const setHasFirstStroke = useSetAtom(hasFirstStrokeAtom);
  const setLockedSeriesId = useSetAtom(lockedSeriesIdAtom);
  const setAnnotationStartTs = useSetAtom(annotationStartTsAtom);
  const setActiveMeasurementTool = useSetAtom(activeMeasurementToolAtom);
  // Ref-mirror of `isSegmentationActive` so the long-lived commit callback
  // (captured at bind time) reads the latest value without re-binding.
  const isSegmentationActive = useAtomValue(isSegmentationActiveAtom);
  const isSegmentationActiveRef = useRef(isSegmentationActive);
  isSegmentationActiveRef.current = isSegmentationActive;
  // Used by the cursor hook (bump tool sizes its cursor by radius), the bump
  // radius sync effect (pushes the value to BumpContourTool config + module
  // fallback so the actual brush size matches the cursor), and the in-flight
  // nnInteractive overlay.
  const bumpRadius = useAtomValue(bumpRadiusAtom);
  const nnLoading = useAtomValue(nnInteractiveLoadingAtom);
  // Guards the seg-setup effect so a series swap re-runs it but re-renders
  // don't. Keyed by segmentationId.
  const segSetupForRef = useRef<string | null>(null);
  // Captures changing callback props so long-lived listeners always read the
  // latest values without re-binding on every render.
  const propsRef = useRef({ onSliceChange, onWindowChange, onZoomChange });
  propsRef.current = { onSliceChange, onWindowChange, onZoomChange };
  // Ref-mirror of the cross-series jump target so the (async) content effect
  // reads the latest value after `setStack` resolves — SET_SERIES seeds this
  // atomically with the series swap, i.e. it's known by the time the stack loads.
  const pendingJumpRef = useRef(pendingJump);
  pendingJumpRef.current = pendingJump;

  // ─── Lifecycle effect — enable viewport, register listeners ──────────────
  useEffect(() => {
    let aborted = false;
    let toolGroupAdded = false;
    const viewportId = viewportIdOverride ?? "stackViewport_default";
    viewportIdRef.current = viewportId;
    const stackNewImageRef: { current: ((evt: any) => void) | null } = { current: null };
    const voiModifiedRef: { current: ((evt: any) => void) | null } = { current: null };
    const cameraModifiedRef: { current: ((evt: any) => void) | null } = { current: null };

    (async () => {
      await initializeCornerstone();
      if (aborted) return;
      const element = elementRef.current;
      if (!element) return;

      // Wait for non-zero element size before enable — cornerstone needs a
      // valid canvas; a 0×0 WebGL surface never recovers.
      while (true) {
        const rect = element.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) break;
        await new Promise((r) => setTimeout(r, 50));
        if (aborted) return;
      }

      const Enums = getEnums();
      const engine = getOrCreateSharedRenderingEngine();
      if (!engine.getViewport(viewportId)) {
        engine.enableElement({
          viewportId,
          type: Enums.ViewportType.STACK,
          element,
          defaultOptions: {
            background: [0, 0, 0] as [number, number, number],
          },
        });
      }

      const { ToolGroupManager } = getCornerstoneTools();
      const toolGroupId = `${SHARED_RENDERING_ENGINE_ID}_toolGroup`;
      let toolGroup = ToolGroupManager.getToolGroup(toolGroupId);
      if (!toolGroup) {
        toolGroup = ToolGroupManager.createToolGroup(toolGroupId);
        if (toolGroup) configureToolGroup(toolGroup);
      }
      if (toolGroup) {
        toolGroup.addViewport(viewportId, SHARED_RENDERING_ENGINE_ID);
        toolGroupAdded = true;
      }

      const onStackNewImage = (evt: any) => {
        const eventViewportId = evt.detail?.viewportId;
        if (eventViewportId !== viewportId) return;
        const imageIdIndex = evt.detail?.imageIdIndex;
        if (typeof imageIdIndex !== "number") return;
        propsRef.current.onSliceChange?.(imageIdIndex, imageIdsRef.current.length);
      };
      element.addEventListener("CORNERSTONE_STACK_NEW_IMAGE" as any, onStackNewImage, true);
      stackNewImageRef.current = onStackNewImage;

      const onVoiModified = (evt: any) => {
        const range = evt.detail?.range;
        if (!range) return;
        const center = Math.round((range.lower + range.upper) / 2);
        const width = Math.round(range.upper - range.lower);
        lastAppliedWindowRef.current = { center, width };
        propsRef.current.onWindowChange?.(center, width);
      };
      element.addEventListener("CORNERSTONE_VOI_MODIFIED" as any, onVoiModified);
      voiModifiedRef.current = onVoiModified;

      const onCameraModified = (evt: any) => {
        const camera = evt.detail?.camera;
        if (!camera?.parallelScale) return;
        const vp = engine.getViewport(viewportId) as any;
        if (!vp) return;
        const initialScale = vp.getDefaultCamera?.()?.parallelScale ?? camera.parallelScale;
        propsRef.current.onZoomChange?.(initialScale / camera.parallelScale);
      };
      element.addEventListener("CORNERSTONE_CAMERA_MODIFIED" as any, onCameraModified);
      cameraModifiedRef.current = onCameraModified;

      if (aborted) return;
      setLifecycleReady(true);
    })().catch((err) => {
      // Mirror of the catch in VolumeViewport: without it the async IIFE's
      // rejection becomes an unhandled promise, lifecycleReady stays false,
      // the content effect bails, and the user sees a black <div> with no
      // error UI. Stable id so this is searchable in CloudWatch.
      if (aborted) return;
      logger.error(
        "[StackViewport] viewer_stack_lifecycle_init_failed seriesId=%s err=%o",
        seriesId,
        err,
      );
      setError("Viewer failed to initialize. Reload the page to retry.");
    });

    return () => {
      aborted = true;
      setLifecycleReady(false);
      const element = elementRef.current;
      if (element) {
        if (stackNewImageRef.current) {
          element.removeEventListener(
            "CORNERSTONE_STACK_NEW_IMAGE" as any,
            stackNewImageRef.current,
            true,
          );
        }
        if (voiModifiedRef.current) {
          element.removeEventListener("CORNERSTONE_VOI_MODIFIED" as any, voiModifiedRef.current);
        }
        if (cameraModifiedRef.current) {
          element.removeEventListener(
            "CORNERSTONE_CAMERA_MODIFIED" as any,
            cameraModifiedRef.current,
          );
        }
      }
      if (toolGroupAdded) {
        try {
          const { ToolGroupManager } = getCornerstoneTools();
          const toolGroupId = `${SHARED_RENDERING_ENGINE_ID}_toolGroup`;
          const tg = ToolGroupManager.getToolGroup(toolGroupId);
          tg?.removeViewports(SHARED_RENDERING_ENGINE_ID, viewportId);
        } catch (e) {
          logger.debug("[StackViewport] removeViewports:", e);
        }
      }
      // Remove segmentation representations on this viewport before disabling
      // it — mirrors VolumeViewport.tsx:727-735. Otherwise a registered rep
      // stays bound to a disabled viewportId on every unmount that ran seg
      // setup (`enableSegmentation` path).
      try {
        const { segmentation } = getCornerstoneTools();
        segmentation.removeSegmentationRepresentations(viewportId, {});
      } catch (e) {
        logger.debug("[StackViewport] removeSegmentationRepresentations:", e);
      }
      disableSharedViewport(viewportId);
      viewportIdRef.current = null;
      imageIdsRef.current = [];
      loadedStackSeriesRef.current = null;
      lastAppliedWindowRef.current = null;
      // Drop the idempotency marker so a subsequent mount re-runs seg setup
      // against the freshly-enabled viewport.
      segSetupForRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── WebGL context loss — surface as user-visible error ──────────────────
  // Mirror of the subscriber in VolumeViewport (self-heal). The shared engine
  // now keeps its canvas and requests restoration, so a lost context recovers:
  // `webglContextLost` shows a TRANSIENT "restoring" state; `webglContextRestored`
  // clears it, drops loadedStackSeriesRef so the content effect treats the series
  // as new and re-issues setStack (re-loading the frame the dropped context lost),
  // and bumps recoveryEpoch to trigger that re-run. A fallback timer escalates to
  // the reload prompt only if restoration never arrives.
  useEffect(() => {
    const clearTimer = () => {
      if (recoveryTimerRef.current) {
        clearTimeout(recoveryTimerRef.current);
        recoveryTimerRef.current = null;
      }
    };
    const onLost = viewerBusOn("webglContextLost", () => {
      contextLostRef.current = true;
      setError("Restoring GPU context…");
      clearTimer();
      recoveryTimerRef.current = setTimeout(() => {
        if (contextLostRef.current) {
          setError("Viewer lost its GPU context. Reload the page to continue.");
        }
      }, RECOVERY_FALLBACK_MS);
    });
    const onRestored = viewerBusOn("webglContextRestored", () => {
      clearTimer();
      contextLostRef.current = false;
      setError(null);
      loadedStackSeriesRef.current = null;
      setRecoveryEpoch((n) => n + 1);
    });
    return () => {
      viewerBusOff("webglContextLost", onLost);
      viewerBusOff("webglContextRestored", onRestored);
      clearTimer();
    };
  }, []);

  // ─── Content effect — swap imageIds on series change ──────────────────────
  useEffect(() => {
    if (!lifecycleReady) return;
    if (contextLostRef.current) return;
    const viewportId = viewportIdRef.current;
    if (!viewportId) return;
    const engine = getOrCreateSharedRenderingEngine();

    if (!studyMetadata?.series?.[seriesId]) {
      setError(`Series not available: ${seriesId}`);
      return;
    }
    if (!studyMetadata.canonical_cdn_base_url) {
      setError("No canonical CDN base URL configured");
      return;
    }
    const instances = studyMetadata.series[seriesId];
    const imageIds = buildS3ImageIds(studyMetadata.canonical_cdn_base_url, instances);
    if (!imageIds.length) {
      setError("No images found in series");
      return;
    }
    imageIdsRef.current = imageIds;
    setError(null);
    // Only (re)build the stack when the series actually changed. This effect
    // also re-runs on windowing / loading-state prop changes; re-issuing
    // setStack there would reset the displayed slice to the start index and
    // clobber a finding-jump (or manual scroll) on the weak-GPU stack fallback.
    const isNewStackSeries = loadedStackSeriesRef.current !== seriesId;

    const cancelled = { current: false };
    (async () => {
      try {
        const vp = engine.getViewport(viewportId) as any;
        if (!vp) return;

        if (isNewStackSeries) {
          // Start at the cross-series jump target (a finding-jump slice) when
          // set, else the middle frame (radiologist expectation for a
          // multi-frame stack: US cine, multi-frame mammography). SET_SERIES
          // seeds pendingJump synchronously on a cross-series swap, so the
          // target is known by the time this effect runs. Without this the
          // weak-GPU stack fallback snapped to the middle slice and a
          // finding-jump to a not-yet-displayed series was silently lost.
          // (Mike Moritz, 2026-05-29.)
          const startIndex = resolveStackStartIndex(pendingJumpRef.current, imageIds.length);
          await vp.setStack(imageIds, startIndex);
          if (cancelled.current) return;
          // #coronal-lr: same radiological-L/R flip the volume paths apply. A
          // mirrored axial that landed on the stack fallback (non-reconstructable
          // geometry, weak GPU) is just as laterality-reversed, and the shared
          // orientation markers read the live camera — so leaving this one host
          // un-flipped would show a correctly-labelled neurological pane next to
          // radiological siblings. Applied after setStack, which resets camera.
          const stackMeta = studyMetadata.series_metadata?.[seriesId];
          shouldFlipHorizontalRef.current = paneNeedsRadiologicalFlip({
            nativeIop: stackMeta?.image_orientation_patient,
            nativePlane: stackMeta ? derivePlane(stackMeta) : null,
          });
          applyRadiologicalFlip(vp, shouldFlipHorizontalRef.current, `StackViewport ${viewportId}`);
          loadedStackSeriesRef.current = seriesId;
          propsRef.current.onSliceChange?.(startIndex, imageIds.length);
          // Stack loads one image at a time; emit a single "displayable" event
          // so overlays unblock without a volume-style streaming progress dance.
          onLoadingStateChange?.(false, imageIds.length, imageIds.length, true);
        }

        if (windowCenter !== undefined && windowWidth !== undefined) {
          vp.setProperties({
            voiRange: {
              lower: windowCenter - windowWidth / 2,
              upper: windowCenter + windowWidth / 2,
            },
          });
          lastAppliedWindowRef.current = { center: windowCenter, width: windowWidth };
        }
        if (isInverted !== undefined) {
          vp.setProperties({ invert: isInverted });
        }
        engine.renderViewports([viewportId]);
      } catch (err) {
        if (cancelled.current) return;
        setError(err instanceof Error ? err.message : "Failed to set stack");
      }
    })();

    return () => {
      cancelled.current = true;
    };
  }, [
    lifecycleReady,
    seriesId,
    studyMetadata,
    windowCenter,
    windowWidth,
    isInverted,
    onLoadingStateChange,
    // Re-issue setStack after a WebGL context loss/restore (frame texture dropped).
    recoveryEpoch,
  ]);

  // ─── Segmentation setup (weak-GPU fallback only) ─────────────────────────
  // The whole seg UI is gated on `segmentationVolumeId` (SegmentationAnnotationPanel)
  // and the contour drawing tools require an active Contour rep
  // (ensureContourSegmentationActive — wired in its own effect below).
  // VolumeViewport does both; the stack path historically did neither, so seg
  // tools were dead on the GPU fallback.
  //
  // Approach: build the source volume in CPU cache (createAndCacheVolume — this
  // is memory, NOT GPU volume rendering, so it does not re-trigger the
  // white-out that put us on the stack path), derive a labelmap volume from
  // it, register it, set the atom (ungates the panel). The stack viewport
  // keeps rendering 2D via setStack; the volume is used only as the labelmap
  // backing for save.
  //
  // Cold-load race: `createAndCacheDerivedVolume` reads source-volume
  // dimensions; on first load those aren't hydrated until enough per-frame
  // metadata streams in. VolumeViewport solves this with a deferred retry
  // fired by CORNERSTONE_IMAGE_VOLUME_LOADING_COMPLETED — we mirror that
  // pattern here (the prior 2 s polling loop silently aborted on slow S3).
  useEffect(() => {
    if (!enableSegmentation || !lifecycleReady) return;
    if (contextLostRef.current) return;
    const viewportId = viewportIdRef.current;
    if (!viewportId) return;
    if (!studyMetadata?.series?.[seriesId] || !studyMetadata.canonical_cdn_base_url) {
      logger.warn(
        "[StackViewport] seg setup skipped: missing series/cdn for seriesId=%s",
        seriesId,
      );
      return;
    }

    const segmentationId = `${studyId}_${seriesId}_segmentation`;
    if (segSetupForRef.current === segmentationId) return;

    const cornerstone = getCornerstone();
    const volumeId = `cornerstoneStreamingImageVolume:${studyId}_${seriesId}`;
    const cancelled = { current: false };
    let onLoadingCompleted: ((evt: any) => void) | null = null;

    // Try to derive the labelmap + register reps. Returns true once the
    // derivation succeeded (or hit an unrecoverable error) so the retry stops.
    const runSetup = async (): Promise<boolean> => {
      // Idempotency: if a prior invocation (initial run or LOADING_COMPLETED
      // listener) already completed this segmentation, skip the
      // re-registration. `addSegmentations` throws on duplicates and the
      // current sequencing makes a second call rare but not impossible
      // (event re-emission, mid-await cleanup races). Defense in depth.
      if (segSetupForRef.current === segmentationId) return true;
      try {
        const { segmentation, Enums: ToolEnums } = getCornerstoneTools();
        const srcVolume: any = cornerstone.cache.getVolume(volumeId);
        const dimsReady = Array.isArray(srcVolume?.dimensions) && srcVolume.dimensions[0] != null;
        if (!dimsReady) return false;

        try {
          await segmentation.removeSegmentation(segmentationId);
        } catch (e) {
          logger.debug("[StackViewport] removeSegmentation (expected if first load):", e);
        }
        if (cancelled.current) return true;

        // The cornerstone volume cache survives `removeSegmentation`
        // (that only unregisters the abstract segmentation). On HMR or a
        // component remount where the segmentationId is unchanged, a second
        // createAndCacheDerivedVolume throws "volumeId already in cache".
        // Re-use the existing derived volume in that case.
        if (!cornerstone.cache.getVolume(segmentationId)) {
          cornerstone.volumeLoader.createAndCacheDerivedVolume(volumeId, {
            volumeId: segmentationId,
          });
        }
        segmentation.addSegmentations([
          {
            segmentationId,
            representation: {
              type: ToolEnums.SegmentationRepresentations.Labelmap,
              data: { volumeId: segmentationId },
            },
          },
        ]);
        segmentation.addSegmentationRepresentations(viewportId, [
          { segmentationId, type: ToolEnums.SegmentationRepresentations.Labelmap },
        ]);
        setSegmentationVolumeId(segmentationId);
        segSetupForRef.current = segmentationId;
        logger.debug("[StackViewport] seg setup succeeded seriesId=%s", seriesId);
        return true;
      } catch (e) {
        // Return false (not true) so the LOADING_COMPLETED listener installed
        // by the caller can retry once the source volume finishes streaming.
        // Transient throws (mid-await cache collision on HMR, race with a
        // sibling pane's removeSegmentation, etc.) shouldn't pin the panel
        // banner up forever — the listener fires at most once per volume
        // load, and the cache + idempotency guards above make the retry safe.
        logger.error("[StackViewport] seg setup attempt failed seriesId=%s err=%o", seriesId, e);
        return false;
      }
    };

    (async () => {
      try {
        const instances = studyMetadata.series[seriesId];
        const imageIds = buildS3ImageIds(studyMetadata.canonical_cdn_base_url, instances);
        if (!imageIds.length) {
          logger.warn("[StackViewport] seg setup skipped: 0 imageIds for seriesId=%s", seriesId);
          return;
        }

        // createAndCacheDerivedVolume reads each source image's
        // rows/columns/spacing from the metadata provider, so the metadata
        // modules must be seeded first. Mirrors VolumeViewport pre-volume.
        const seriesMeta = studyMetadata.series_metadata?.[seriesId];
        registerImagePlaneMetadataForSeries(
          studyMetadata.canonical_cdn_base_url,
          instances,
          seriesMeta,
        );
        registerImagePixelMetadataForSeries(studyMetadata.canonical_cdn_base_url, instances);
        registerGeneralSeriesMetadataForSeries(
          studyMetadata.canonical_cdn_base_url,
          instances,
          seriesMeta,
        );
        registerVoiLutMetadataForSeries(
          studyMetadata.canonical_cdn_base_url,
          instances,
          seriesMeta,
        );

        let volume: any = cornerstone.cache.getVolume(volumeId);
        if (!volume) {
          volume = await cornerstone.volumeLoader.createAndCacheVolume(volumeId, { imageIds });
          if (cancelled.current) return;
          // Fire-and-forget — `load()` does not return a Promise; completion
          // is signalled via CORNERSTONE_IMAGE_VOLUME_LOADING_COMPLETED.
          volume.load();
        }

        const done = await runSetup();
        if (done || cancelled.current) return;

        // Dimensions not hydrated yet → defer to the loading-completed event.
        onLoadingCompleted = async (evt: any) => {
          if (cancelled.current) return;
          if (evt?.detail?.volumeId && evt.detail.volumeId !== volumeId) return;
          const ok = await runSetup();
          if (ok && onLoadingCompleted) {
            cornerstone.eventTarget.removeEventListener(
              "CORNERSTONE_IMAGE_VOLUME_LOADING_COMPLETED" as any,
              onLoadingCompleted,
            );
            onLoadingCompleted = null;
          }
        };
        if (cancelled.current) return;
        cornerstone.eventTarget.addEventListener(
          "CORNERSTONE_IMAGE_VOLUME_LOADING_COMPLETED" as any,
          onLoadingCompleted,
        );
        logger.debug(
          "[StackViewport] seg setup deferred to LOADING_COMPLETED for seriesId=%s",
          seriesId,
        );
      } catch (e) {
        logger.error("[StackViewport] seg setup outer failed seriesId=%s err=%o", seriesId, e);
      }
    })();

    return () => {
      cancelled.current = true;
      if (onLoadingCompleted) {
        cornerstone.eventTarget.removeEventListener(
          "CORNERSTONE_IMAGE_VOLUME_LOADING_COMPLETED" as any,
          onLoadingCompleted,
        );
        onLoadingCompleted = null;
      }
      // Clear the atom + idempotency marker only when the atom still points
      // at THIS effect's segmentation. Sibling MPR panes also write the atom
      // (last-writer-wins); clobbering unconditionally on every cleanup would
      // wipe an active pane's value. Leaves the stale-on-remount bug fixed
      // without breaking multi-pane cases.
      if (segmentationVolumeIdRef.current === segmentationId) {
        setSegmentationVolumeId(null);
      }
      if (segSetupForRef.current === segmentationId) {
        segSetupForRef.current = null;
      }
    };
  }, [
    enableSegmentation,
    lifecycleReady,
    seriesId,
    studyId,
    studyMetadata,
    setSegmentationVolumeId,
  ]);

  // ─── Contour rep activation ──────────────────────────────────────────────
  // Separate from the heavy seg-setup effect so activeSegmentIndex changes
  // (segment switch / color update) don't tear down the labelmap derivation.
  // Mirrors VolumeViewport.tsx:1290-1293.
  useEffect(() => {
    if (!segmentationVolumeId || !lifecycleReady) return;
    ensureContourSegmentationActive(segmentationVolumeId, activeSegmentIndex);
  }, [segmentationVolumeId, activeSegmentIndex, lifecycleReady]);

  // ─── Bump radius → tool config sync ──────────────────────────────────────
  // The brush size has two consumers: a `toolGroup.setToolConfiguration`
  // entry the tool reads on mouse-down (primary), and a module-level
  // fallback in BumpContourTool.ts. Without this wire-up the slider only
  // updates the cursor preview — the actual push radius would stay at
  // whatever VolumeViewport last wrote (or the default 28), which is exactly
  // the "push larger than visible" symptom on the stack fallback.
  // Mirrors VolumeViewport.tsx:1268-1283.
  useEffect(() => {
    setBumpRadiusModule(bumpRadius);
    if (!lifecycleReady) return;
    try {
      const { ToolGroupManager } = getCornerstoneTools();
      const toolGroupId = `${SHARED_RENDERING_ENGINE_ID}_toolGroup`;
      const tg = ToolGroupManager.getToolGroup(toolGroupId) as any;
      tg?.setToolConfiguration?.("BumpContourTool", { radius: bumpRadius });
    } catch (e) {
      logger.debug("[StackViewport] bump radius setToolConfiguration skipped:", e);
    }
  }, [bumpRadius, lifecycleReady]);

  // ─── Region-grow commit + auto-switch callbacks ──────────────────────────
  // Flips the form's drawing-state gates when a seed click lands on this
  // stack pane (Save button enables, series locks, HU overlay populates).
  // Mirrors VolumeViewport.tsx:1307-1423 minus the cross-plane jumpToWorld
  // block — that primitive is volume-only, and stack panes in a hanging
  // protocol are independent series anyway.
  useEffect(() => {
    const vpId = viewportIdRef.current;
    if (!segmentationVolumeId || !vpId) return;
    // The full callback signature is `(currentImageId: string | null) => void`;
    // VolumeViewport reads `currentImageId` to drive cross-plane jumpToWorld.
    // Stack panes in a hanging protocol are independent series, so cross-plane
    // navigation isn't applicable — we deliberately ignore the arg here.
    setRegionGrowCommitCallback(vpId, () => {
      if (!isSegmentationActiveRef.current) return;
      setIsDrawing(true);
      setHasFirstStroke(true);
      setLockedSeriesId(seriesId);
      setAnnotationStartTs((prev: string | null) => prev ?? new Date().toISOString());
    });
    setRegionGrowAutoSwitchCallback(vpId, () => {
      if (!isSegmentationActiveRef.current) return;
      setActiveMeasurementTool("bump");
    });
    return () => {
      setRegionGrowCommitCallback(vpId, null);
      setRegionGrowAutoSwitchCallback(vpId, null);
    };
  }, [
    segmentationVolumeId,
    seriesId,
    setIsDrawing,
    setHasFirstStroke,
    setLockedSeriesId,
    setAnnotationStartTs,
    setActiveMeasurementTool,
  ]);

  // Apply windowing prop changes (preset buttons, atom updates).
  useEffect(() => {
    if (!lifecycleReady || windowCenter === undefined || windowWidth === undefined) return;
    if (
      lastAppliedWindowRef.current &&
      lastAppliedWindowRef.current.center === windowCenter &&
      lastAppliedWindowRef.current.width === windowWidth
    ) {
      return;
    }
    const viewportId = viewportIdRef.current;
    if (!viewportId) return;
    const engine = getOrCreateSharedRenderingEngine();
    const vp = engine.getViewport(viewportId) as any;
    if (!vp) return;
    vp.setProperties({
      voiRange: { lower: windowCenter - windowWidth / 2, upper: windowCenter + windowWidth / 2 },
    });
    vp.render();
    lastAppliedWindowRef.current = { center: windowCenter, width: windowWidth };
  }, [windowCenter, windowWidth, lifecycleReady]);

  // External slice navigation (from hanging-protocol slot, finding click).
  useEffect(() => {
    if (!lifecycleReady || externalSliceIndex === undefined) return;
    const viewportId = viewportIdRef.current;
    if (!viewportId) return;
    const engine = getOrCreateSharedRenderingEngine();
    const vp = engine.getViewport(viewportId) as any;
    if (!vp) return;
    const total = imageIdsRef.current.length;
    if (total <= 1) return;
    if (externalSliceIndex < 0 || externalSliceIndex >= total) return;
    try {
      // Capture zoom/pan BEFORE the slice change. Cornerstone's setImageIdIndex
      // runs an internal resetCamera + presentation round-trip that DRIFTS when
      // adjacent slices differ in geometry (spacing/rows/cols), snapping the
      // image back toward fit-to-window on each tick — the annotator report
      // "scroll resets the image to normal size". Re-apply the captured
      // presentation after to pin the user's zoom/pan (same get/setViewPresentation
      // pattern as resizeAndRestoreViewports; restores both zoom AND pan).
      const pres = vp.getViewPresentation?.();
      vp.setImageIdIndex(externalSliceIndex);
      if (pres) {
        vp.setViewPresentation?.(pres);
        vp.render?.();
      }
    } catch (err) {
      logger.debug("[StackViewport] setImageIdIndex:", err);
    }
  }, [externalSliceIndex, lifecycleReady]);

  // Reset camera (pan + zoom).
  useEffect(() => {
    if (!resetCameraTrigger || !lifecycleReady) return;
    const viewportId = viewportIdRef.current;
    if (!viewportId) return;
    const engine = getOrCreateSharedRenderingEngine();
    const vp = engine.getViewport(viewportId) as any;
    vp?.resetCamera({ resetZoom: true, resetPan: true });
    // `resetCamera` unconditionally clears `flipHorizontal`, so re-assert the
    // radiological flip — same hole the hanging-protocol Reset View handler
    // closes. Unreachable today (no caller passes `resetCameraTrigger`; see the
    // intentionally-unwired region in ProtocolViewport), and wired here anyway so
    // whoever does hook it up doesn't silently un-mirror a mirrored study.
    applyRadiologicalFlip(vp, shouldFlipHorizontalRef.current, "StackViewport:reset");
    engine.renderViewports([viewportId]);
  }, [resetCameraTrigger, lifecycleReady]);

  // Bind the active measurement tool to the primary mouse button when the
  // toolbar picks one. Without this, the stack viewport's tool group stays
  // on the default WindowLevel binding and Length/RectangleROI buttons in
  // the toolbar toggle but never produce annotations on click. Delete-key
  // cleanup of those annotations already works without changes — the global
  // useAnnotationSelectionMirror in CaseViewer mirrors Cornerstone's
  // selection events into selectedAnnotationsAtom and the
  // deleteSelectedAnnotation command consumes them regardless of viewport
  // kind.
  //
  // Non-safe tools (lasso / bump / regionGrow) need a contour-segmentation
  // rep that only VolumeViewport sets up. In a mixed hanging-protocol scene
  // (stack pane co-mounted with a volume pane), a VolumeViewport effect can
  // bind one of those tools to the SHARED toolGroup; clicking on the stack
  // pane would then throw "A contour segmentation must be active". To keep
  // the binding coherent across both viewport kinds we actively fall back
  // to "none" (unbinds primary) on non-safe selections, plus emit a stable
  // warn id for ops telemetry.
  useEffect(() => {
    if (!lifecycleReady) return;
    try {
      const { ToolGroupManager } = getCornerstoneTools();
      const toolGroupId = `${SHARED_RENDERING_ENGINE_ID}_toolGroup`;
      const toolGroup = ToolGroupManager.getToolGroup(toolGroupId);
      if (!toolGroup) return;
      // Seg tools (regionGrow/lasso/bump) are normally unsafe on stack panes,
      // but become safe once `enableSegmentation` has set up a contour rep on
      // this viewport (weak-GPU fallback, reconstructable series).
      const isSafe =
        STACK_SAFE_TOOLS.has(activeMeasurementTool) ||
        (enableSegmentation && SEG_TOOLS.has(activeMeasurementTool));
      if (!isSafe) {
        // The hanging-protocol viewer uses a shared tool group across stack
        // and volume panes. A stack pane without its own segmentation
        // representation must not reset a lasso/bump selection made for the
        // segmentation-enabled volume pane, otherwise the UI shows an active
        // tool while the shared group is actually bound to WindowLevel.
        if (SEG_TOOLS.has(activeMeasurementTool)) return;
        logger.warn(
          "[StackViewport] stack_unsafe_tool_selected tool=%s, falling back to none",
          activeMeasurementTool,
        );
        switchActiveTool(toolGroup, "none");
        return;
      }
      switchActiveTool(toolGroup, activeMeasurementTool);
    } catch (e) {
      logger.warn("[StackViewport] switch_active_tool_failed:", e);
    }
  }, [activeMeasurementTool, lifecycleReady, enableSegmentation]);

  // Prevent page scroll when wheeling over viewport. Cornerstone's own
  // wheel listener is passive by default; without preventDefault the
  // outer page also scrolls.
  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };
    element.addEventListener("wheel", handleWheel, { passive: false });
    return () => element.removeEventListener("wheel", handleWheel);
  }, []);

  // Drive the per-tool cursor (region-grow target, bump radius, nnInteractive
  // refine marker, etc.) on the viewport element. Cornerstone overrides the
  // CSS `cursor` property internally, so the hook reapplies on tool change.
  useSegmentationCursor(elementRef, activeMeasurementTool, bumpRadius);

  // nnInteractive in-flight overlay — matches VolumeViewport so the stack
  // fallback also surfaces "Running AI segmentation…" while the server call is
  // pending. Gated on this viewport id so companion panes only spin when their
  // own contour is in flight.
  //
  // TODO(stack-nn-companion): the spinner UI here is correct, but
  // nnInteractive's companion-pane projection currently fails on stack mode —
  // `computeCompanionBbox` / `snapCompanionSeed` / `worldToNativeVoxelZYX`
  // read `viewport.getImageData()`, which on a StackViewport is a single 2D
  // frame (dims [W,H,1], degenerate `spacing[2]`) → garbage through-plane
  // indices. Fix is to use the cached source-volume geometry per series
  // (the same `cornerstoneStreamingImageVolume:${studyId}_${seriesId}` we
  // build for the labelmap) instead of the viewport's imageData. Tracked
  // separately from this PR.
  const thisViewportId = viewportIdRef.current ?? viewportIdOverride ?? null;
  const showNnSpinner =
    nnLoading.isLoading &&
    thisViewportId !== null &&
    (nnLoading.viewportId === thisViewportId ||
      (nnLoading.companionViewportIds ?? []).includes(thisViewportId));

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-bg-2 text-warn">
        <div className="text-center">
          <p className="text-lg font-semibold">Error</p>
          <p className="text-sm mt-2">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full bg-black">
      <div ref={elementRef} data-measure-target="true" className="w-full h-full" />
      {showNnSpinner && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/50 backdrop-blur-[1px] pointer-events-none"
          data-testid="nn-interactive-spinner"
        >
          <div className="h-10 w-10 rounded-full border-4 border-signal-stroke border-t-transparent animate-spin" />
          <div className="text-sm text-white/90 font-medium tracking-wide">
            Running AI segmentation…
          </div>
        </div>
      )}
    </div>
  );
};

export default StackViewport;
